import {
  Box,
  Grid,
  IconButton,
  makeStyles,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Lightbox from 'react-awesome-lightbox';
import 'react-awesome-lightbox/build/style.css';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { buildHourGroups, captureTsOf, channelOf } from './minuteGrid';

const addressCache = {};

class CustomLightbox extends Lightbox {
  constructor(props) {
    super(props);

    this.applyZoom = (type) => {
      let { zoomStep = 0.3 } = this.props;
      const oldZoom = this.state.zoom;
      let newZoom = oldZoom;

      switch (type) {
        case "in":
          newZoom = oldZoom + zoomStep;
          break;
        case "out":
          newZoom = oldZoom - zoomStep;
          if (newZoom < 1) {
            if (oldZoom > 1) {
              newZoom = 1;
            } else {
              return;
            }
          }
          break;
        case "reset":
          this.resetZoom();
          return;
      }

      if (newZoom === 1) {
        this.lastX = 0;
        this.lastY = 0;
        this.setState({ x: 0, y: 0, zoom: 1 });
      } else {
        const ratio = newZoom / oldZoom;
        const nextX = this.state.x * ratio;
        const nextY = this.state.y * ratio;
        this.lastX = nextX;
        this.lastY = nextY;
        this.setState({
          zoom: newZoom,
          x: nextX,
          y: nextY
        });
      }
    };

    const originalStartMove = this.startMove;
    this.startMove = (e) => {
      this.lastX = this.state.x;
      this.lastY = this.state.y;
      originalStartMove(e);
    };
  }
}

const useStyles = makeStyles(theme => ({
  container: {
    height: 'calc(100vh - 66px)',
    overflowY: 'auto',
    padding: '24px 30px',
    backgroundColor: '#ffffff',
    color: '#12202f',
    boxSizing: 'border-box',
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#e4e6eb',
      borderRadius: '4px',
    },
    [theme.breakpoints.down('sm')]: {
      padding: '16px'
    }
  },
  groupSection: {
    marginBottom: '32px',
  },
  groupHeader: {
    fontSize: '16px',
    fontWeight: 700,
    color: '#0066cc',
    borderBottom: '1px solid #e4e6eb',
    paddingBottom: '8px',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  clusterRow: {
    marginBottom: '20px',
  },
  clusterLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#65676b',
    marginBottom: '8px',
    letterSpacing: '0.2px',
  },
  clusterLabelMuted: {
    color: '#b6bcc4',
    fontStyle: 'italic',
  },
  noImageCard: {
    backgroundColor: '#ffffff',
    border: '1px dashed #d8dce1',
    borderRadius: '10px',
    overflow: 'hidden',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  noImageWrapper: {
    position: 'relative',
    width: '100%',
    paddingTop: '56.25%',
    backgroundColor: '#f5f6f8',
    overflow: 'hidden',
  },
  noImageInner: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
  },
  noImageText: {
    fontSize: '11px',
    fontWeight: 500,
    color: '#b6bcc4',
  },
  noImageBadge: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    backgroundColor: '#e4e6eb',
    color: '#8a9099',
    fontSize: '10px',
    fontWeight: 600,
    padding: '1px 7px',
    borderRadius: '8px',
    lineHeight: '16px',
    zIndex: 2,
  },
  noImageMuted: {
    color: '#b6bcc4',
  },
  imageCard: {
    backgroundColor: '#ffffff',
    border: '1px solid #e4e6eb',
    borderRadius: '10px',
    overflow: 'hidden',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    transition: 'transform 0.2s, box-shadow 0.2s, border-color 0.2s',
    cursor: 'pointer',
    '&:hover': {
      borderColor: '#0066cc',
      boxShadow: '0 8px 24px rgba(0, 102, 204, 0.12)',
      transform: 'translateY(-2px)',
    },
    '&:hover $hoverOverlay': {
      opacity: 1,
      pointerEvents: 'auto',
    },
  },
  imageWrapper: {
    position: 'relative',
    width: '100%',
    paddingTop: '56.25%',
    backgroundColor: '#f0f2f5',
    overflow: 'hidden',
  },
  cardImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  hoverOverlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: '34px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 8px',
    backgroundColor: 'rgba(0,0,0,0.42)',
    opacity: 0,
    transition: 'opacity 0.18s ease',
    pointerEvents: 'none',
  },
  overlayBtn: {
    color: '#ffffff',
    background: 'transparent',
    padding: 4,
    '&:hover': {
      background: 'transparent'
    }
  },
  timeBadge: {
    position: 'absolute',
    top: '8px',
    left: '8px',
    backgroundColor: 'rgba(255,255,255,0.92)',
    color: '#12202f',
    fontSize: '11px',
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: '4px',
    zIndex: 2,
    border: '1px solid #e4e6eb',
    whiteSpace: 'nowrap',
  },
  cameraBadge: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    backgroundColor: '#9e9e9e',
    color: '#fff',
    fontSize: '11px',
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: '4px',
    zIndex: 2,
  },
  overlayCameraPill: {
    backgroundColor: 'rgba(158,158,158,0.9)',
    color: '#ffffff',
    fontSize: '10px',
    fontWeight: 600,
    padding: '1px 4px',
    borderRadius: '8px',
    lineHeight: 1,
    letterSpacing: '0.2px'
  },
  cardDetails: {
    padding: '10px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    backgroundColor: '#ffffff',
    flex: 1,
  },
  cardTitle: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#12202f',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  cardSub: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '6px',
    fontSize: '11px',
    color: '#65676b',
    // allow address to wrap to multiple lines
    overflow: 'hidden',
  },
  cardSubIcon: {
    width: '13px',
    height: '13px',
    flexShrink: 0,
  },
  cardSubText: {
    fontSize: '11px',
    color: '#65676b',
    // allow up to 2 lines for address
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  emptyBox: {
    height: 'calc(100vh - 66px)',
    backgroundColor: theme.palette.background.dark,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
      padding: '16px'
    }
  },
  emptyContent: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

const ImageCard = React.memo(({ image, index, onZoom, onDownload }) => {
  const classes = useStyles({});
  const [address, setAddress] = useState(image.device?.location || '--');

  useEffect(() => {
    if (image.device?.location && image.device.location !== '--') {
      setAddress(image.device.location);
      return;
    }

    const lat = image.device?.position?.lat;
    const lng = image.device?.position?.lng;
    const hasCoords = image.device?.position?.hasCoords;

    if (!hasCoords || !lat || !lng) {
      setAddress('--');
      return;
    }

    const cacheKey = `${lat.toFixed(6)},${lng.toFixed(6)}`;
    if (addressCache[cacheKey]) {
      if (addressCache[cacheKey] instanceof Promise) {
        addressCache[cacheKey].then(addr => setAddress(addr));
      } else {
        setAddress(addressCache[cacheKey]);
      }
      return;
    }

    const fetchAddress = async () => {
      try {
        const response = await axios.get(
          `https://map.vgps.vn/reverse?lat=${lat}&lon=${lng}&format=json`
        );
        const data = response.data;
        let displayName = (data?.display_name || "").trim();
        if (displayName) {
          const postcode = data?.address?.postcode || "";
          if (postcode) {
            displayName = displayName.replaceAll(`, ${postcode}`, "");
          }
          displayName = displayName.trim();
        } else {
          displayName = '--';
        }
        addressCache[cacheKey] = displayName;
        setAddress(displayName);
        return displayName;
      } catch (error) {
        console.error('Failed to reverse geocode:', error);
        setAddress('--');
        return '--';
      }
    };

    addressCache[cacheKey] = fetchAddress();
  }, [
    image.device?.location,
    image.device?.position?.lat,
    image.device?.position?.lng,
    image.device?.position?.hasCoords
  ]);

  const handleZoom = e => {
    e.stopPropagation();
    onZoom(index);
  };

  const handleDownload = e => {
    e.stopPropagation();
    onDownload(image.url, image.download_url);
  };

  return (
    <Box className={classes.imageCard} onClick={() => onZoom(index)}>
      <Box className={classes.imageWrapper}>
        <img
          src={image.thumbnailUrl}
          alt={image.file_name}
          className={classes.cardImage}
          loading="lazy"
        />

        <Box className={classes.hoverOverlay}>
          <Box>
            {image.camera && (
              <span className={classes.overlayCameraPill}>
                {image.camera.replace('Camera ', 'Cam ')}
              </span>
            )}
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <IconButton
              className={classes.overlayBtn}
              onClick={handleDownload}
              size="small"
              aria-label="Tải xuống"
              title="Tải xuống"
            >
              <GetAppIcon style={{ fontSize: 16 }} />
            </IconButton>
            <IconButton
              className={classes.overlayBtn}
              onClick={handleZoom}
              size="small"
              aria-label="Xem ảnh lớn"
              title="Xem ảnh lớn"
            >
              <FullscreenIcon style={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>
      </Box>
      <Box className={classes.cardDetails}>
        <Typography className={classes.cardTitle} title={image.created_at}>
          {image.created_at || '--'}
        </Typography>
        <Box className={classes.cardSub}>
          <img
            src="/static/iconSvg/iconLocationRed.svg"
            alt="location"
            className={classes.cardSubIcon}
          />
          <span className={classes.cardSubText} title={address}>
            {address}
          </span>
        </Box>
      </Box>
    </Box>
  );
});

ImageCard.propTypes = {
  image: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  onZoom: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
};

/**
 * Ô giữ chỗ cho kênh không có ảnh trong cụm. Cùng khung và cùng tỉ lệ với
 * ImageCard để cột không bị lệch; không click, không zoom, không tải.
 */
const NoImageCard = React.memo(({ channel }) => {
  const classes = useStyles({});
  return (
    <Box className={classes.noImageCard} aria-label={`Camera ${channel} không có ảnh`}>
      <Box className={classes.noImageWrapper}>
        <Box className={classes.noImageInner}>
          <svg
            width="26"
            height="26"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#b6bcc4"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <circle cx="9" cy="10" r="1.6" />
            <path d="M21 15l-5-5-5.5 5.5" />
            <line x1="3" y1="21" x2="21" y2="3" />
          </svg>
          <span className={classes.noImageText}>Không có ảnh</span>
        </Box>
        <span className={classes.noImageBadge}>{`Cam ${channel}`}</span>
      </Box>
      <Box className={classes.cardDetails}>
        <Typography className={`${classes.cardTitle} ${classes.noImageMuted}`}>
          --
        </Typography>
      </Box>
    </Box>
  );
});

NoImageCard.propTypes = {
  channel: PropTypes.number.isRequired
};

const DEFAULT_CENTER = [21.024683, 105.832495];

Result.propTypes = {
  isShowResult: PropTypes.bool.isRequired,
  isSidebarOpen: PropTypes.bool.isRequired
};

function Result({ isShowResult, isSidebarOpen, onCloseSidebar }) {
  const classes = useStyles({ isSidebarOpen });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isOpenImage, setIsOpenImage] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [dataPhotoView, setDataPhotoView] = useState({});
  const [visibleHours, setVisibleHours] = useState(2);
  const containerRef = useRef(null);

  const listVehicleTracking = useSelector(
    state => state.vehicleSlice?.listVehicleTracking || []
  );

  const listLicensePlateCamera = useSelector(
    state => state.vehicleSlice?.listLicensePlateCamera || []
  );

  const rawImages = useSelector(
    state => state.imageSlice?.listImage || []
  );
  // Error message set by imageSlice.rejected — surfaced in the empty state
  // below so a failed search (token expired, network, etc.) doesn't silently
  // fall back to the previous result.
  const imageErr = useSelector(state => state.imageSlice?.err);
  // Memo hoá cả tầng map: prop `listImages` là đầu vào của gom cụm bên dưới,
  // dựng lại mỗi render sẽ làm mọi memo phía sau vô hiệu.
  const listImages = useMemo(() => {
  const sortedImages = (rawImages || []).slice()
    .sort((a, b) => captureTsOf(b) - captureTsOf(a));

  return sortedImages.map((image, originalIndex) => {
    const actualSerial = image.serial || image.tenant;
    const vehicleInfo = listVehicleTracking.find(
      v => v.device_serial === actualSerial
    ) || listLicensePlateCamera.find(
      v => v.serial === actualSerial
    );
    let cameraDisplay = image.camera || `Camera ${image.camera_num || '--'}`;
    if (image.camera) {
      const camStr = image.camera.toString();
      const cameraMatch = camStr.match(/CAM(\d+)/i);
      if (cameraMatch) {
        cameraDisplay = `Camera ${cameraMatch[1]}`;
      } else if (camStr.length === 3 && camStr.endsWith('01')) {
        cameraDisplay = `Camera ${camStr.charAt(0)}`;
      } else if (['1', '2', '3', '4'].includes(camStr)) {
        cameraDisplay = `Camera ${camStr}`;
      } else {
        cameraDisplay = `Camera ${camStr}`;
      }
    }

    let displayTime = '--';
    const captureTime = moment.utc(image.capture_time);

    if (image.capture_time && captureTime.isValid()) {
      displayTime = captureTime.add(7, 'hours').format('DD-MM-YYYY HH:mm:ss');
    }

    if (displayTime === '--') {
      if (image.created_at && moment(image.created_at).isValid()) {
        displayTime = moment(image.created_at).add(7, 'hours').format('DD-MM-YYYY HH:mm:ss');
      } else if (image.device_time) {
        const deviceTime = moment.unix(image.device_time);
        if (deviceTime.isValid()) {
          displayTime = deviceTime.add(7, 'hours').format('DD-MM-YYYY HH:mm:ss');
        }
      }
    }

    const latVal = parseFloat(image.latitude || image.lat);
    const lngVal = parseFloat(image.longitude || image.lng);
    const hasCoords = !isNaN(latVal) && !isNaN(lngVal) && latVal !== 0 && lngVal !== 0;

    return {
      id: image.id || image.filename,
      url: image.url || '/static/empty.png',
      thumbnailUrl: image.url || '/static/empty.png',
      download_url: image.download_url || image.url || '',
      file_name: image.filename || image.file_name,
      created_at: displayTime,
      camera: cameraDisplay,
      // Dùng cho gom cụm + xếp ô; `_originalIndex` là chỉ số vào chính mảng
      // này, lightbox đọc lại qua `listImages[index]`.
      _originalIndex: originalIndex,
      ts: captureTsOf(image),
      channel: channelOf(image),
      hourKey: displayTime !== '--' ? displayTime.slice(9, 11) : '--',
      device: {
        vehicle: {
          license_plate: vehicleInfo?.license_plate || actualSerial || '--'
        },
        location: image.location || '--',
        position: {
          lat: hasCoords ? latVal : DEFAULT_CENTER[0],
          lng: hasCoords ? lngVal : DEFAULT_CENTER[1],
          hasCoords
        },
        device_status: image.device?.status || '--',
        direction: 0
      }
    };
  });
  }, [rawImages, listVehicleTracking, listLicensePlateCamera]);

  // Tập kênh suy từ TOÀN BỘ result của cửa sổ tìm kiếm, không phải từng cụm —
  // tính theo cụm thì cụm thiếu ảnh sẽ tự co lại và mất đúng ô No Image cần
  // hiện. Khi backend trả `channel_count` thì chỉ phải đổi đúng chỗ này.
  const channels = useMemo(() => {
    const set = new Set();
    listImages.forEach(img => {
      if (img.channel != null) set.add(img.channel);
    });
    return Array.from(set).sort((a, b) => a - b);
  }, [listImages]);

  // Gom cụm trước, rồi mới chia giờ — lazy-load vẫn dừng đúng ở mép giờ.
  const groupedImages = useMemo(
    () => buildHourGroups(listImages, channels),
    [listImages, channels]
  );

  const allSortedHours = useMemo(
    () =>
      Object.keys(groupedImages).sort((a, b) => {
        if (a === '--') return 1;
        if (b === '--') return -1;
        return Number(b) - Number(a);
      }),
    [groupedImages]
  );

  // Mục tiêu: LUÔN 4 ảnh trên một hàng, bất kể thiết bị có mấy kênh.
  //
  // Một cụm (cluster) là một mốc thời gian và chứa đúng một ô cho mỗi kênh, nên
  // riêng nó không lấp đủ hàng khi thiết bị chỉ có 1–2 kênh. Vì vậy xếp NHIỀU
  // CỤM cạnh nhau: mỗi cụm là một khối rộng 12/clustersPerRow, bên trong chia
  // tiếp cho các kênh của nó. 2 kênh → 2 cụm/hàng → 4 ảnh; 4 kênh → 1 cụm/hàng
  // → 4 ảnh; 1 kênh → 4 cụm/hàng → 4 ảnh. 3 kênh không chia hết cho 4 nên giữ
  // 1 cụm/hàng (3 ảnh) thay vì cắt một cụm làm đôi.
  const IMAGES_PER_ROW = 4;
  const slotCols = Math.min(Math.max(channels.length, 1), IMAGES_PER_ROW);
  const clustersPerRow = Math.max(1, Math.floor(IMAGES_PER_ROW / slotCols));
  const clusterWidth = 12 / clustersPerRow;
  // Bề rộng ô BÊN TRONG cụm — tính theo lưới của cụm, không phải của hàng.
  const slotWidth = 12 / slotCols;

  useEffect(() => {
    setVisibleHours(2);
  }, [rawImages]);

  useEffect(() => {
    if (visibleHours >= allSortedHours.length) return;
    const checkFillScreen = () => {
      const el = containerRef.current;
      if (el && el.scrollHeight - el.clientHeight < 200) {
        setVisibleHours(prev => Math.min(prev + 2, allSortedHours.length));
      }
    };
    const timer = setTimeout(checkFillScreen, 150);
    return () => clearTimeout(timer);
  }, [visibleHours, allSortedHours.length, isShowResult]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 200) {
      if (visibleHours < allSortedHours.length) {
        setVisibleHours(prev => Math.min(prev + 2, allSortedHours.length));
      }
    }
  };

  // Only the first N whole hours are rendered; each rendered hour is complete.
  const sortedHours = allSortedHours.slice(0, visibleHours);

  /**
   * Reuse the SAME presigned URL the <img> already loaded, so the browser
   * cache serves the bytes locally instead of round-tripping to storage a
   * second time with a different presign. Storage CPU drops by 1 HMAC sign
   * per actual download, and bandwidth drops by 1 GET per download.
   *
   * Falls back to the legacy `downloadUrl` only if `imageUrl` is missing
   * (defensive — list response always includes `url`).
   */
  const handleDownload = async (imageUrl, downloadUrl) => {
    const targetUrl = imageUrl || downloadUrl;
    if (!targetUrl) return;
    const fileName = targetUrl.split('/').pop().split('?')[0] || 'image.jpg';
    try {
      // Same URL as <img src={image.url}> → browser cache hit, no storage call.
      const response = await fetch(targetUrl);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      window.open(targetUrl, '_blank');
    }
  };

  const handleZoom = (index) => {
    setPhotoIndex(index);
    setDataPhotoView(listImages[index] || {});
    setIsOpenImage(true);
  };

  const [showControls, setShowControls] = useState(true);
  const controlsTimeoutRef = useRef(null);

  const resetControlsTimer = useCallback(() => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 2500);
  }, []);

  useEffect(() => {
    if (isOpenImage) {
      resetControlsTimer();
      const handleActivity = () => {
        resetControlsTimer();
      };
      window.addEventListener('mousemove', handleActivity);
      window.addEventListener('touchmove', handleActivity);
      window.addEventListener('click', handleActivity);
      window.addEventListener('keydown', handleActivity);
      return () => {
        window.removeEventListener('mousemove', handleActivity);
        window.removeEventListener('touchmove', handleActivity);
        window.removeEventListener('click', handleActivity);
        window.removeEventListener('keydown', handleActivity);
        if (controlsTimeoutRef.current) {
          clearTimeout(controlsTimeoutRef.current);
        }
      };
    }
  }, [isOpenImage, resetControlsTimer]);

  return (
    <>
      {isShowResult && (
        <>
          {listImages.length === 0 ? (
            <Box className={classes.emptyBox}>
              <Box className={classes.emptyContent}>
                <Box>
                  <img src="/static/empty.png" alt="empty" />
                </Box>
                <Typography>
                  <h4>{imageErr ? imageErr : 'Danh sách trống'}</h4>
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box ref={containerRef} className={classes.container} onScroll={handleScroll}>
              {sortedHours.map(hour => {
                const group = groupedImages[hour] || { clusters: [], unknown: [] };
                const hourLabel =
                  hour !== '--'
                    ? `${hour.padStart(2, '0')}:00 – ${(Number(hour) + 1).toString().padStart(2, '0')}:00`
                    : `Không xác định giờ`;

                return (
                  <Box className={classes.groupSection} key={hour}>
                    <Typography className={classes.groupHeader}>
                      <img
                        src="/static/iconSvg/iconTime.svg"
                        alt="time"
                        style={{ width: '16px', height: '16px' }}
                      />
                      {hourLabel}
                    </Typography>

                    <Grid container spacing={2}>
                      {group.clusters.map(cluster => (
                        <Grid
                          item
                          key={cluster.anchorTs}
                          lg={clusterWidth}
                          md={clusterWidth}
                          sm={12}
                          xs={12}
                        >
                          <Box className={classes.clusterRow}>
                            <Grid container spacing={2}>
                              {cluster.slots.map(slot => (
                                <Grid
                                  item
                                  key={slot.channel}
                                  lg={slotWidth}
                                  md={slotWidth}
                                  sm={6}
                                  xs={12}
                                >
                                  {slot.image ? (
                                    <ImageCard
                                      image={slot.image}
                                      index={slot.image._originalIndex}
                                      onZoom={handleZoom}
                                      onDownload={handleDownload}
                                    />
                                  ) : (
                                    <NoImageCard channel={slot.channel} />
                                  )}
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>

                    {group.unknown.length > 0 && (
                      <Box className={classes.clusterRow}>
                        <Typography
                          className={`${classes.clusterLabel} ${classes.clusterLabelMuted}`}
                        >
                          Không rõ kênh
                        </Typography>
                        <Grid container spacing={2}>
                          {group.unknown.map(image => (
                            <Grid
                              item
                              key={image.id}
                              lg={12 / IMAGES_PER_ROW}
                              md={12 / IMAGES_PER_ROW}
                              sm={6}
                              xs={12}
                            >
                              <ImageCard
                                image={image}
                                index={image._originalIndex}
                                onZoom={handleZoom}
                                onDownload={handleDownload}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </>
      )}

      {isOpenImage && (
        <div className={showControls ? "" : "lb-controls-hidden"}>
          <CustomLightbox
            images={listImages}
            startIndex={photoIndex}
            onClose={() => setIsOpenImage(false)}
          />
          <Box
            className="lb-custom-left-header"
            style={{
              position: 'fixed',
              top: '0px',
              left: isMobile ? '10px' : '30px',
              height: '50px',
              display: 'flex',
              alignItems: 'center',
              zIndex: 99999,
              pointerEvents: 'none',
            }}
          >
            <Box style={{ display: 'flex', gap: '15px', alignItems: 'center', pointerEvents: 'auto' }}>
              <Box style={{ cursor: 'pointer' }}>
                <img src="/static/iconSvg/image-active-icon.svg" alt="Image" />
              </Box>
              <Box>
                <Typography style={{ color: '#ffffff', fontSize: '14px', fontWeight: 500 }}>
                  {dataPhotoView.created_at}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box
            className="custom-download-btn"
            onClick={() => handleDownload(dataPhotoView.url, dataPhotoView.download_url)}
            title="Tải xuống"
          >
            <img
              style={{ width: '20px', height: '20px' }}
              src="/static/iconSvg/downloadIcon.svg"
              alt="Download"
            />
          </Box>
        </div>
      )}
    </>
  );
}

export default Result;
